import moment from "moment"
import React, { useEffect, useState } from "react"
import { useHistory, useLocation } from "react-router-dom"

import {
   ExportTimelineGeneratePdf,
   GetInventoryWarning,
   GetProductionLineDetails,
   GetProductionLinesListing,
   GetTimeLineListing,
} from "api/api.service"

import ProductionScheduleCalendar from "./ProductionScheduleCalendar"
import { useLocalPaginatedRequest } from "hooks/useLocalPaginatedRequest"
import { ProductionScheduleProvider } from "./ProductionScheduleContext"
import { dateTimeFormat, parseDate } from "utils/dateFormatUtils"
import ProductionScheduleHeader from "./ProductionScheduleHeader"
import useAsyncSelect from "hooks/useAsyncSelect"
import { isEmpty } from "utils/commonUtils"
import { APP_ROUTES, getRoute } from "helpers/routeHelpers"
import ProductionLineStatus from "constants/ProductionLineStatus"
import { downloadPdf } from "utils/downloadUtils"
import toast from "react-hot-toast"
import ExportDateModal from "../Modals/ExportDateModal/ExportDateModal"

const DATA_KEY = "data"
const TOTAL_KEY = "total"
const UNLIMITED_PRODUCTION_LINE_LIMIT = -1

const initialRangeValue = [
   {
      startDate: undefined,
      endDate: undefined,
      key: "selection",
   },
]

const ProductionSchedule = () => {
   let controller = new AbortController()
   let signal = controller.signal

   const location = useLocation()
   const history = useHistory()
   const { id: timeLineId, productionLineId: productionLineId } =
      (location && location.state) || {}

   const [isLoading, setIsLoading] = useState(false)
   const [productionDetailsData, setProductionDetailsData] = useState({})
   const [productionFilter, setProductionFilter] = useState(null)

   const [range, setRange] = useState(initialRangeValue)
   const [tempRange, setTempRange] = useState(initialRangeValue)
   const [showWarningModal, setShowWarningModal] = useState(false)
   const [isFullScreenView, setIsFullScreenView] = useState(false)
   const [isExporting, setIsExporting] = useState(false)
   const [showExportDateModal, setShowExportDateModal] = useState(false)
   useEffect(() => {
      const className = "app-fullscreen"
      if (isFullScreenView) {
         document.body.classList.add(className)
      } else {
         document.body.classList.remove(className)
      }
      return () => {
         document.body.classList.remove(className)
      }
   }, [isFullScreenView])

   const params = {
      limit: -1,
      productionLineId: productionFilter?.id ?? productionLineId,
      ...getDateRangeParams(range[0]),
   }

   function getDateRangeParams(dateRange) {
      if (!dateRange) return {}
      const params = {}
      if (dateRange.startDate) {
         params.dateFrom = moment
            .utc(dateRange.startDate)
            .startOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      if (dateRange.endDate) {
         params.dateTo = moment
            .utc(dateRange.endDate)
            .endOf("day")
            .format(dateTimeFormat.apiDateTimeFormat)
      }
      return params
   }

   const { data: timeLineData, isFetching } = useLocalPaginatedRequest({
      requestFn: getTimeLineListing,
      params,
      deps: [
         productionFilter?.id,
         range,
         productionLineId,
         productionDetailsData?.startDate,
      ],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
   })

   async function getTimeLineListing() {
      if (!productionLineId && (!productionFilter?.id || !range?.[0]?.key)) {
         return Promise.resolve([])
      }

      const res = await GetTimeLineListing(params)
      const timelineData = res?.data

      if (!isEmpty(timelineData)) {
         const dates = timelineData.map(item => moment.utc(item.date))
         // const minDate = moment.min(dates)
         const minDate = new Date(productionDetailsData?.startDate)
         const maxDate = moment.max(dates)

         if (productionDetailsData?.startDate) {
            setTempRange([
               {
                  startDate: minDate,
                  endDate: maxDate.toDate(),
                  key: "selection",
                  type: "auto",
               },
            ])
         }
      } else {
         if (!!productionDetailsData?.stages?.length) {
            const minDate = parseDate(productionDetailsData?.startDate)

            const maxDate = productionDetailsData?.startDate
               ? parseDate(productionDetailsData?.endDate)
               : moment(productionDetailsData?.startDate)
                    .add(6, "days")
                    .toDate()
            setTempRange([
               {
                  startDate: minDate,
                  endDate: maxDate,
                  key: "selection",
                  type: "auto",
               },
            ])
         } else {
            setTempRange(initialRangeValue)
         }
      }

      return res
   }

   useEffect(() => {
      if (productionFilter?.id || productionLineId) {
         fetchProductionLineDetails(productionFilter?.id ?? productionLineId)
      }
   }, [productionFilter?.id, productionLineId])

   const fetchProductionLineDetails = async id => {
      setIsLoading(true)
      GetProductionLineDetails(id)
         .then(detailsRes => {
            if (detailsRes) {
               if (!productionFilter) {
                  const filterValue = {
                     id: detailsRes?.id,
                     label: detailsRes?.name,
                     value: detailsRes?.id,
                  }
                  setProductionFilter(filterValue)
               }
               setProductionDetailsData(detailsRes)
            }
         })
         .catch(err => {
            console.error(err)
         })
         .finally(() => {
            setTimeout(() => setIsLoading(false), 500)
         })
   }

   const {
      selectEntity: productionListing,
      handleSearchOnOptions,
      handleSelectAOption,
   } = useAsyncSelect({
      apiFunc: GetProductionLinesListing,
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
      labelKey: "name",
      valueKey: "id",
      deps: [],
      params: {
         status: ProductionLineStatus.Active,
         limit: UNLIMITED_PRODUCTION_LINE_LIMIT,
      },
      attachSignal: true,
   })

   useEffect(() => {
      if (productionFilter) {
         handleSelectAOption(productionFilter, productionFilter.id)
      }
   }, [productionFilter])

   const handleSetProduction = option => {
      if (typeof option !== "object" || !option) return
      if (option.value === productionFilter?.value) return

      setProductionFilter(option)
      setShowWarningModal(false)
      setRange(initialRangeValue)
      setTempRange(initialRangeValue)
      handleSelectAOption(option, option.id)
   }

   const handleSearch = (value = "") => {
      handleSearchOnOptions(value)
   }

   const handleDateChange = ({ selection }) => {
      const { startDate, endDate } = selection

      let safeEndDate = endDate

      if (!(endDate instanceof Date) || isNaN(endDate)) {
         safeEndDate = startDate
      }

      const adjustedSelection = {
         ...selection,
         endDate: safeEndDate,
      }

      setRange([adjustedSelection])
      setTempRange(initialRangeValue)
   }

   const navigateToDetails = () => {
      history.push(
         getRoute(APP_ROUTES.PRODUCTION_LINE_MANAGEMENT_DETAILS, {
            productionLineId: productionFilter?.id ?? productionLineId,
         })
      )
   }
   const handleExport = async selectedDate => {
      const currentProductionLineId = productionFilter?.id ?? productionLineId
      const startDate = selectedDate
         ? moment(selectedDate)
              .startOf("day")
              .format(dateTimeFormat.apiDateTimeFormat)
         : moment().startOf("day").format(dateTimeFormat.apiDateTimeFormat)

      if (!currentProductionLineId) {
         toast.error("Please select a production line")
         return
      }

      try {
         setIsExporting(true)
         const params = {
            productionLineId: currentProductionLineId,
            startDate: startDate,
         }

         const response = await ExportTimelineGeneratePdf(params)
         await downloadPdf(response)
         toast.success("Timeline PDF exported successfully!")
      } catch (error) {
         console.error("Error exporting timeline PDF:", error)
         toast.error("Failed to export timeline PDF. Please try again.")
      } finally {
         setIsExporting(false)
      }
   }

   const handleExportClick = () => {
      setShowExportDateModal(true)
   }

   const handleExportDateConfirm = selectedDate => {
      handleExport(selectedDate)
   }

   return (
      <ProductionScheduleProvider
         value={{
            timeLineId,
            timeLineData,
            overrides: productionDetailsData?.overrides,
            dailyConfigurations: productionDetailsData?.dailyConfigurations,
            stages: productionDetailsData?.stages,
         }}
      >
         <ProductionScheduleHeader
            isLoading={isLoading}
            range={range}
            tempRange={tempRange}
            productionFilter={productionFilter}
            productionListing={productionListing}
            onDateChange={handleDateChange}
            onSetProduction={handleSetProduction}
            onSearch={handleSearch}
            onShowNextInventory={() => setShowWarningModal(prev => !prev)}
            onShowFullScreen={() => setIsFullScreenView(prev => !prev)}
            isDatePickerDisabled={!productionFilter?.id && !productionLineId}
            productionDetailsData={productionDetailsData}
            navigateToDetails={navigateToDetails}
            onExport={handleExportClick}
            isExporting={isExporting}
         />
         <ProductionScheduleCalendar
            isLoading={isLoading || isFetching}
            timeLineData={timeLineData}
            dateRange={range}
            productionDetailsData={productionDetailsData}
            showWarningModal={showWarningModal}
            setShowWarningModal={setShowWarningModal}
            productionLineId={productionFilter?.id ?? productionLineId}
         />

         <ExportDateModal
            isOpen={showExportDateModal}
            onClose={() => setShowExportDateModal(false)}
            onExport={handleExportDateConfirm}
            selectedDate={new Date()}
            minDate={parseDate(productionDetailsData?.startDate)}
         />
      </ProductionScheduleProvider>
   )
}

export default ProductionSchedule
